import { supabase } from '@/integrations/supabase/client';

export type DownloadType = 'document' | 'test_pdf' | 'case_pdf' | 'flashcards_pdf' | 'resumen' | 'slides' | 'infografia' | 'chat_export';
export type DownloadMode = 'funcionarios' | 'oposiciones' | 'oposiciones_c1' | 'oposiciones_c2' | 'oposiciones_iipp' | 'oposiciones_guardia_civil' | 'oposiciones_policia_nacional' | 'base_comun';

export interface UserDownload {
  id: string;
  user_id: string;
  title: string;
  download_type: DownloadType;
  mode: DownloadMode;
  file_url: string | null;
  metadata: Record<string, any>;
  created_at: string;
}

const DOWNLOAD_TYPE_LABELS: Record<DownloadType, string> = {
  document: 'Documento',
  test_pdf: 'Test PDF',
  case_pdf: 'Caso práctico',
  flashcards_pdf: 'Flashcards',
  resumen: 'Resumen',
  slides: 'Presentación',
  infografia: 'Infografía',
  chat_export: 'Export. chat',
};

export function getDownloadTypeLabel(type: DownloadType): string {
  return DOWNLOAD_TYPE_LABELS[type] || type;
}

export function normalizeDownloadMode(mode?: string | null): DownloadMode {
  switch (mode) {
    case 'oposiciones':
    case 'oposiciones_c1':
    case 'oposiciones_c2':
    case 'oposiciones_iipp':
    case 'oposiciones_guardia_civil':
    case 'oposiciones_policia_nacional':
    case 'base_comun':
      return mode;
    case 'base-comun':
    case 'generico':
    case 'oposiciones_generico':
      return 'base_comun';
    case 'policia-nacional':
    case 'policia_nacional':
      return 'oposiciones_policia_nacional';
    case 'guardia-civil':
    case 'guardia_civil':
      return 'oposiciones_guardia_civil';
    case 'iipp':
      return 'oposiciones_iipp';
    case 'c1':
      return 'oposiciones_c1';
    case 'c2':
      return 'oposiciones_c2';
    case 'secretaria':
      return 'oposiciones';
    default:
      return 'funcionarios';
  }
}

export function getDownloadModeFromPathname(pathname?: string): DownloadMode {
  const path = pathname || (typeof window !== 'undefined' ? window.location.pathname : '');
  if (path.includes('/base-comun')) return 'base_comun';
  if (path.includes('/guardia-civil')) return 'oposiciones_guardia_civil';
  if (path.includes('/policia-nacional')) return 'oposiciones_policia_nacional';
  if (path.includes('/oposiciones-c1')) return 'oposiciones_c1';
  if (path.includes('/oposiciones-c2')) return 'oposiciones_c2';
  if (path.includes('/oposiciones-iipp')) return 'oposiciones_iipp';
  if (path.includes('/oposiciones')) return 'oposiciones';
  return 'funcionarios';
}

export function getDownloadModeFromCuerpo(cuerpo: string): DownloadMode {
  switch (cuerpo) {
    case 'generico':
    case 'base_comun':
      return 'base_comun';
    case 'guardia_civil':
      return 'oposiciones_guardia_civil';
    case 'policia_nacional':
      return 'oposiciones_policia_nacional';
    case 'c1':
      return 'oposiciones_c1';
    case 'c2':
      return 'oposiciones_c2';
    case 'iipp':
      return 'oposiciones_iipp';
    case 'secretaria':
      return 'oposiciones';
    default:
      return 'funcionarios';
  }
}

export async function saveDownload(params: {
  userId: string;
  title: string;
  downloadType: DownloadType;
  mode: DownloadMode;
  fileUrl?: string;
  metadata?: Record<string, any>;
}): Promise<void> {
  const { error } = await supabase
    .from('user_downloads')
    .insert({
      user_id: params.userId,
      title: params.title,
      download_type: params.downloadType,
      mode: params.mode,
      file_url: params.fileUrl || null,
      metadata: params.metadata || {},
    });

  if (error) {
    console.error('Error saving download:', error);
    return;
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('munilex:download-saved'));
  }
}

export async function listDownloads(
  userId: string,
  mode: DownloadMode
): Promise<UserDownload[]> {
  let query = supabase
    .from('user_downloads')
    .select('*')
    .eq('user_id', userId);

  if (mode === 'funcionarios') {
    query = query.eq('mode', 'funcionarios');
  } else {
    // Para cualquier área de oposiciones, mostrar todas las descargas de oposiciones del usuario
    query = query.neq('mode', 'funcionarios');
  }

  const { data, error } = await query
    .order('created_at', { ascending: false })
    .limit(100);

  if (error) {
    console.error('Error listing downloads:', error);
    return [];
  }
  return (data || []) as UserDownload[];
}

export async function listDownloadsByType(
  userId: string,
  mode: DownloadMode,
  types: DownloadType[]
): Promise<UserDownload[]> {
  let query = supabase
    .from('user_downloads')
    .select('*')
    .eq('user_id', userId)
    .in('download_type', types);

  if (mode === 'funcionarios') {
    query = query.eq('mode', 'funcionarios');
  } else {
    query = query.neq('mode', 'funcionarios');
  }

  const { data, error } = await query
    .order('created_at', { ascending: false })
    .limit(50);

  if (error) {
    console.error('Error listing downloads by type:', error);
    return [];
  }
  return (data || []) as UserDownload[];
}

export async function deleteDownload(id: string): Promise<void> {
  const { error } = await supabase
    .from('user_downloads')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

/**
 * Re-download a previously saved item by regenerating the file from stored metadata.
 */
export async function redownloadItem(dl: UserDownload): Promise<boolean> {
  const meta = dl.metadata || {};

  const triggerDownload = (url: string, defaultName: string) => {
    try {
      const link = document.createElement('a');
      link.href = url;
      link.download = defaultName;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const cleanName = (dl.title ? dl.title.replace(/[^\w\s-]/gi, '').trim() : '') || 'documento';

  if (dl.file_url) {
    const ext = dl.file_url.split('?')[0].endsWith('.docx') ? '.docx' : '.pdf';
    triggerDownload(dl.file_url, `${cleanName}${ext}`);
    return true;
  }
  if (meta.view_url) {
    window.location.assign(meta.view_url);
    return true;
  }
  if (meta.export_url) {
    triggerDownload(meta.export_url, `${cleanName}.pdf`);
    return true;
  }

  switch (dl.download_type) {
    case 'document':
    case 'resumen': {
      if (!meta.content) return false;
      const { exportToWord } = await import('@/services/exportService');
      await exportToWord(meta.content, dl.title, dl.mode as any);
      return true;
    }

    case 'chat_export': {
      if (meta.content) {
        const { exportToWord } = await import('@/services/exportService');
        await exportToWord(meta.content, dl.title, dl.mode as any);
        return true;
      }
      if (meta.chat_id) {
        // Navigate to the chat — caller handles navigation
        return false;
      }
      return false;
    }

    case 'slides':
    case 'infografia': {
      // Priorizar export_url (PDF público en CDN de Gamma).
      // gamma_url es el editor en gamma.app y requiere login en esa cuenta → evitar.
      if (meta.export_url) {
        window.open(meta.export_url, '_blank', 'noopener,noreferrer');
        return true;
      }
      if (meta.content) {
        const { exportToPDF } = await import('@/services/exportService');
        await exportToPDF(meta.content, `${dl.title || 'material'}.pdf`, dl.mode as any);
        return true;
      }
      if (meta.gamma_url) {
        // Último recurso: link al editor de Gamma (puede pedir login en gamma.app).
        const { toast } = await import('sonner');
        toast.warning('Este material antiguo abre el editor de Gamma y puede pedir iniciar sesión. Vuelve a generarlo para obtener un PDF descargable.');
        window.open(meta.gamma_url, '_blank', 'noopener,noreferrer');
        return true;
      }
      return false;
    }

    case 'test_pdf':
    case 'case_pdf':
    case 'flashcards_pdf': {
      // These PDFs are generated on-the-fly from complex data; we can't easily re-generate.
      // If content was stored, export as Word fallback.
      if (meta.content) {
        const { exportToWord } = await import('@/services/exportService');
        await exportToWord(meta.content, dl.title, dl.mode as any);
        return true;
      }
      return false;
    }

    default:
      return false;
  }
}
